import { useState } from "react";
import { useAuth } from "@clerk/react";
import {
  useListTimetables, useCreateTimetable, useDeleteTimetable,
  getListTimetablesQueryKey,
} from "@workspace/api-client-react";
import { useArchiveTimetable } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { useDiary } from "@/contexts/DiaryContext";
import { useLocation } from "wouter";
import { format } from "date-fns";
import {
  BookOpen, Plus, Trash2, Archive, ArchiveX, Download,
  ChevronDown, ChevronRight, Calendar, CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

// ─── New Diary Dialog ─────────────────────────────────────────────────────────

function NewDiaryDialog({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: (id: number) => void;
}) {
  const createTimetable = useCreateTimetable();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName]           = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");

  const presets = [
    { label: "2027 — Term 1", name: "2027 — Term 1", start: "2027-01-31", end: "2027-04-11" },
    { label: "2027 — Term 2", name: "2027 — Term 2", start: "2027-04-28", end: "2027-07-04" },
    { label: "2027 — Term 3", name: "2027 — Term 3", start: "2027-07-19", end: "2027-09-26" },
    { label: "2027 — Term 4", name: "2027 — Term 4", start: "2027-10-12", end: "2027-12-19" },
  ];

  const applyPreset = (p: typeof presets[0]) => {
    setName(p.name); setStartDate(p.start); setEndDate(p.end);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const t = await createTimetable.mutateAsync({ data: { name, startDate, endDate } });
      queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
      toast({ title: "Diary created" });
      onCreated(t.id);
      setName(""); setStartDate(""); setEndDate("");
      onClose();
    } catch (err) {
      toast({
        title: "Couldn't create the diary",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>New Diary</DialogTitle></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Quick presets</Label>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => (
                <button
                  key={p.label} type="button"
                  onClick={() => applyPreset(p)}
                  className="text-xs px-2.5 py-1 rounded-full border border-gray-200 hover:border-primary hover:text-primary hover:bg-primary/5 transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 2027 — Term 1" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Start date *</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label>End date *</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={createTimetable.isPending} data-write-action>
              {createTimetable.isPending ? "Creating…" : "Create Diary"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Diary row ────────────────────────────────────────────────────────────────

function DiaryRow({ timetable, isActive, onActivate, onArchive, onExport, onDelete }: {
  timetable: any;
  isActive: boolean;
  onActivate: () => void;
  onArchive: () => void;
  onExport: () => void;
  onDelete: () => void;
}) {
  const fmtDate = (d: string) => format(new Date(d + "T00:00:00"), "d MMM yyyy");
  const archived = timetable.archived;

  return (
    <div className={`bg-white border rounded-xl p-4 flex items-center gap-4 group transition-shadow hover:shadow-sm ${
      isActive ? "border-primary/30 ring-1 ring-primary/20" : "border-gray-200"
    } ${archived ? "opacity-60" : ""}`}>
      <div className={`p-2 rounded-lg flex-shrink-0 ${archived ? "bg-gray-100" : "bg-primary/10"}`}>
        <BookOpen className={`h-5 w-5 ${archived ? "text-gray-400" : "text-primary"}`} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-gray-900 truncate">{timetable.name}</p>
          {isActive && <Badge className="text-[10px] bg-primary/10 text-primary border-0 px-1.5">Active</Badge>}
          {archived && <Badge variant="outline" className="text-[10px] text-gray-400 px-1.5">Archived</Badge>}
        </div>
        <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
          <Calendar className="h-3 w-3" />
          {timetable.startDate && timetable.endDate
            ? `${fmtDate(timetable.startDate)} – ${fmtDate(timetable.endDate)}`
            : "No dates set"}
        </p>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        {!archived && !isActive && (
          <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={onActivate}>
            <CheckCircle2 className="h-3.5 w-3.5" />
            Open
          </Button>
        )}
        <Button
          variant="ghost" size="icon"
          className="h-8 w-8 text-gray-400 hover:text-primary"
          title="Export this diary"
          onClick={onExport}
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost" size="icon"
          className={`h-8 w-8 ${archived ? "text-gray-400 hover:text-green-600" : "text-gray-400 hover:text-amber-600"}`}
          title={archived ? "Unarchive" : "Archive"}
          onClick={onArchive}
        >
          {archived ? <ArchiveX className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
        </Button>
        <Button
          variant="ghost" size="icon"
          className="h-8 w-8 text-gray-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Delete diary"
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function Diaries() {
  const { getToken } = useAuth();
  const { data: timetables = [], isLoading } = useListTimetables();
  const deleteTimetable  = useDeleteTimetable();
  const archiveTimetable = useArchiveTimetable();
  const { activeTimetableId, setActiveTimetableId } = useDiary();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isNewOpen, setIsNewOpen]         = useState(false);
  const [showArchived, setShowArchived]   = useState(false);

  const active   = timetables.filter((t: any) => !t.archived);
  const archived = timetables.filter((t: any) => t.archived);

  const handleArchive = async (t: any) => {
    await archiveTimetable.mutateAsync({ timetableId: t.id, data: { archived: !t.archived } });
    queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
    toast({ title: t.archived ? "Diary unarchived" : "Diary archived" });
  };

  const handleDelete = async (t: any) => {
    if (!confirm(`Delete "${t.name}" and all its lesson plans? This cannot be undone.`)) return;
    await deleteTimetable.mutateAsync({ timetableId: t.id });
    queryClient.invalidateQueries({ queryKey: getListTimetablesQueryKey() });
    toast({ title: "Diary deleted" });
  };

  const handleActivate = (t: any) => {
    setActiveTimetableId(t.id);
    setLocation("/app/diary");
    toast({ title: `Switched to ${t.name}` });
  };

  const handleExport = async (t: any) => {
    const name = encodeURIComponent(t.name);
    const fromParam = t.startDate ? `&dateFrom=${t.startDate}` : "";
    const toParam   = t.endDate   ? `&dateTo=${t.endDate}`     : "";
    const url  = `/api/exports/diary.html?timetableId=${t.id}&name=${name}${fromParam}${toParam}`;
    try {
      const token = await getToken();
      const res   = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onload = () => {
        const w = window.open();
        if (w) { w.document.write(reader.result as string); w.document.close(); }
      };
      reader.readAsText(blob);
    } catch (err) {
      // api-error-helper-exempt: raw fetch (not ApiError); surfaces helper-style fallback.
      toast({
        title: "Export failed",
        description: getApiErrorMessage(err, "Please try again in a moment."),
        variant: "destructive",
      });
    }
  };

  const handleCreated = (id: number) => {
    setActiveTimetableId(id);
    setLocation("/app/timetable-settings");
    toast({ title: "Diary created — set up your timetable structure" });
  };

  return (
    <div className="p-4 sm:p-6 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Diaries</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your term and year diaries</p>
        </div>
        <Button className="gap-2" onClick={() => setIsNewOpen(true)} data-write-action>
          <Plus className="h-4 w-4" />New Diary
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      )}

      {!isLoading && timetables.length === 0 && (
        <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No diaries yet</h3>
          <p className="text-gray-500 mb-4">Create your first diary to start planning.</p>
          <Button onClick={() => setIsNewOpen(true)} data-write-action>Create First Diary</Button>
        </div>
      )}

      {/* Active diaries */}
      {active.length > 0 && (
        <div className="space-y-3 mb-6">
          {active.map((t: any) => (
            <DiaryRow
              key={t.id}
              timetable={t}
              isActive={t.id === activeTimetableId}
              onActivate={() => handleActivate(t)}
              onArchive={() => handleArchive(t)}
              onExport={() => handleExport(t)}
              onDelete={() => handleDelete(t)}
            />
          ))}
        </div>
      )}

      {/* Archived diaries */}
      {archived.length > 0 && (
        <div>
          <button
            onClick={() => setShowArchived((v) => !v)}
            className="flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors mb-3"
          >
            {showArchived ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Archived ({archived.length})
          </button>
          {showArchived && (
            <div className="space-y-3">
              {archived.map((t: any) => (
                <DiaryRow
                  key={t.id}
                  timetable={t}
                  isActive={false}
                  onActivate={() => handleActivate(t)}
                  onArchive={() => handleArchive(t)}
                  onExport={() => handleExport(t)}
                  onDelete={() => handleDelete(t)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <NewDiaryDialog
        open={isNewOpen}
        onClose={() => setIsNewOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}
